import { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import { useApi, githubAuthApiRef } from '@backstage/core-plugin-api';
import { TenantConfig, BusinessUnit } from './types';
import { createDefaultConfig, MIN_MODULE_VERSION, MODULE_VERSION_CONSTRAINT } from './defaults';
import { fetchFileContent } from '../shared';
import { parseHcl, extractModuleBlocks } from '../services/hclParser';

/** Compare two semver-like version strings. Returns <0 if a<b, 0 if equal, >0 if a>b. */
function compareVersions(a: string, b: string): number {
  // Strip leading ~ > v characters
  const cleanA = a.replace(/^[~>v\s]+/, '').trim();
  const cleanB = b.replace(/^[~>v\s]+/, '').trim();
  const partsA = cleanA.split('.').map(Number);
  const partsB = cleanB.split('.').map(Number);
  for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
    const diff = (partsA[i] || 0) - (partsB[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

interface EditFilePickerProps {
  owner: string;
  config: TenantConfig;
  businessUnits: BusinessUnit[];
  onBuChange: (repo: string) => void;
  onConfigLoaded: (config: TenantConfig, file: string) => void;
}

interface RepoFile {
  name: string;
  path: string;
}

function tenantNameFromFile(filename: string): string {
  return filename.replace(/^tenant_/, '').replace(/\.tf$/, '');
}

/**
 * Safely extract a value from a parsed HCL structure.
 * HCL parser may wrap values in arrays (e.g., { key: [value] }).
 */
function unwrap(val: unknown): unknown {
  if (Array.isArray(val) && val.length === 1) return val[0];
  return val;
}

function unwrapStr(val: unknown): string {
  const u = unwrap(val);
  if (typeof u === 'string') return u;
  return String(u ?? '');
}

function unwrapBool(val: unknown): boolean {
  const u = unwrap(val);
  if (typeof u === 'boolean') return u;
  if (u === 'true') return true;
  if (u === 'false') return false;
  return Boolean(u);
}

function unwrapObj(val: unknown): Record<string, unknown> {
  const u = unwrap(val);
  if (u && typeof u === 'object' && !Array.isArray(u)) return u as Record<string, unknown>;
  return {};
}

function unwrapArr(val: unknown): unknown[] {
  const u = unwrap(val);
  if (Array.isArray(u)) return u;
  return [];
}

/**
 * Extract TenantConfig values from a parsed HCL module block.
 * The HCL parser may nest values in arrays (HCL block syntax).
 */
function hclToConfig(
  moduleData: Record<string, unknown>,
  tenantName: string,
  buRepo: string,
): TenantConfig {
  console.log('[EditFilePicker] hclToConfig input:', JSON.stringify(moduleData, null, 2).substring(0, 3000));
  const base = createDefaultConfig();
  base.business_unit_repo = buRepo;
  base.tenant_name = tenantName;

  // Budget
  const budget = unwrapObj(moduleData.budget);
  if (budget.amount != null) base.budget_amount = Number(unwrap(budget.amount)) || base.budget_amount;
  if (budget.calendar_period != null) base.budget_calendar_period = unwrapStr(budget.calendar_period);
  if (moduleData.lifetime_budget != null) base.lifetime_budget = Number(unwrap(moduleData.lifetime_budget)) || base.lifetime_budget;

  // Branching model
  const bm = unwrapObj(moduleData.branching_model);
  if (bm.trunk_based != null) {
    base.branching_model = 'trunk_based';
    const tb = unwrapObj(bm.trunk_based);
    base.deployment_trigger = (unwrapStr(tb.deployment_trigger) as 'push' | 'tag') || 'push';
  } else if (bm.environment_based != null) {
    base.branching_model = 'environment_based';
  }

  // Topics
  const rawTopics = unwrapArr(moduleData.topics);
  if (rawTopics.length > 0) {
    const topics = rawTopics.map(t => String(t));
    base.topics = topics.filter(t => !t.toLowerCase().startsWith('expires-'));
    const expiresTopic = topics.find(t => t.toLowerCase().startsWith('expires-'));
    if (expiresTopic) {
      base.project_end_date = expiresTopic;
    } else {
      // No expires — clear it
      base.project_end_date = '';
    }
  }

  // Environments — preserve per-environment APIs
  // HCL parser may return: [{ "env-name": [{ ...config }] }] or { "env-name": { ...config } }
  const rawEnvs = moduleData.environments;
  const envs = unwrapObj(rawEnvs);
  console.log('[EditFilePicker] environments raw:', JSON.stringify(rawEnvs)?.substring(0, 1000));
  console.log('[EditFilePicker] environments unwrapped:', JSON.stringify(envs)?.substring(0, 1000));
  if (Object.keys(envs).length > 0) {
    base.environments = Object.entries(envs).map(([name, raw]) => {
      const envConfig = unwrapObj(raw);
      console.log(`[EditFilePicker] env ${name} config:`, JSON.stringify(envConfig)?.substring(0, 500));
      const labels = unwrapObj(envConfig.labels);

      // Known keys managed by the UI
      const knownKeys = new Set([
        'enable_billing_user_role', 'service_account_roles', 'additional_apis', 'labels',
        'org_id', 'billing_account_id', 'parent_folder_id',
      ]);

      // Capture all unknown keys as extra passthrough
      const extra: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(envConfig)) {
        if (!knownKeys.has(key)) {
          extra[key] = unwrap(value);
        }
      }

      return {
        name,
        enable_billing_user_role: unwrapBool(envConfig.enable_billing_user_role),
        service_account_roles: unwrapArr(envConfig.service_account_roles).map(String),
        additional_apis: unwrapArr(envConfig.additional_apis).map(String),
        labels: Object.entries(labels)
          .filter(([k]) => k !== 'expires')
          .map(([key, value]) => ({ key, value: String(value) })),
        extra,
      };
    });
    // Global APIs = deduplicated union across all environments
    const allApis = new Set<string>();
    for (const env of base.environments) {
      for (const api of env.additional_apis) {
        allApis.add(api);
      }
    }
    base.additional_apis = Array.from(allApis).sort();
    // Labels from first environment
    if (base.environments.length > 0) {
      base.labels = base.environments[0].labels;
    }
  }

  // Teams
  const teams = unwrapObj(moduleData.teams);
  if (Object.keys(teams).length > 0) {
    base.teams = Object.entries(teams).map(([name, raw]) => {
      const teamConfig = unwrapObj(raw);
      return {
        name,
        permission: (unwrapStr(teamConfig.permission) as 'read' | 'triage' | 'write' | 'maintain' | 'admin') || 'write',
      };
    });
  }

  // Template
  const template = unwrapObj(moduleData.template);
  if (Object.keys(template).length > 0) {
    base.template_repo = unwrapStr(template.repository);
    base.include_all_branches = unwrapBool(template.include_all_branches);
  }

  // TFC
  const tfc = unwrapObj(moduleData.tfc);
  if (Object.keys(tfc).length > 0) {
    base.share_state = unwrapBool(tfc.share_state);
  }

  // Ownership
  if (moduleData.owner_name != null) base.owner_name = unwrapStr(moduleData.owner_name);
  if (moduleData.cost_center != null) base.cost_center = unwrapStr(moduleData.cost_center);
  if (moduleData.owner_email != null) base.owner_email = unwrapStr(moduleData.owner_email);
  if (moduleData.owner_team != null) base.owner_team = unwrapStr(moduleData.owner_team);

  // Purpose & metadata
  if (moduleData.purpose != null) base.purpose = unwrapStr(moduleData.purpose);
  if (moduleData.description != null) base.description = unwrapStr(moduleData.description);
  if (moduleData.project_expiry != null) {
    base.project_expiry = unwrapStr(moduleData.project_expiry);
    base.project_end_date = unwrapStr(moduleData.project_expiry);
  }
  if (moduleData.project_creation != null) base.project_creation = unwrapStr(moduleData.project_creation);
  if (moduleData.lifetime_budget != null) base.lifetime_budget = Number(unwrap(moduleData.lifetime_budget)) || base.lifetime_budget;

  return base;
}

export function EditFilePicker({
  owner,
  config,
  businessUnits,
  onBuChange,
  onConfigLoaded,
}: EditFilePickerProps) {
  const githubAuth = useApi(githubAuthApiRef);
  const [files, setFiles] = useState<RepoFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<string>('');
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [versionUpgraded, setVersionUpgraded] = useState<string | null>(null);

  const selectableUnits = businessUnits.filter(bu => bu.repo !== null);
  const selectedBu = businessUnits.find(bu => bu.repo === config.business_unit_repo) || null;

  // Fetch tenant files when BU is selected
  useEffect(() => {
    if (!config.business_unit_repo) {
      setFiles([]);
      return;
    }

    const load = async () => {
      setFilesLoading(true);
      setError(null);
      try {
        const token = await githubAuth.getAccessToken(['repo']);
        const response = await fetch(
          `https://api.github.com/repos/${owner}/${config.business_unit_repo}/contents/terraform/tenants`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/vnd.github.v3+json',
            },
          },
        );
        if (!response.ok) {
          if (response.status === 404) {
            setFiles([]);
            return;
          }
          throw new Error(`Failed to list files: ${response.statusText}`);
        }
        const data = await response.json();
        const tfFiles = (data as RepoFile[])
          .filter(f => f.name.startsWith('tenant_') && f.name.endsWith('.tf'))
          .sort((a, b) => a.name.localeCompare(b.name));
        setFiles(tfFiles);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to list tenant files');
      } finally {
        setFilesLoading(false);
      }
    };
    load();
  }, [githubAuth, owner, config.business_unit_repo]);

  // Load config from selected file
  const handleFileSelect = async (_: unknown, value: string | null) => {
    if (!value || !config.business_unit_repo) return;
    setSelectedFile(value);
    setLoadingConfig(true);
    setError(null);

    try {
      const token = await githubAuth.getAccessToken(['repo']);
      const content = await fetchFileContent(
        token,
        owner,
        config.business_unit_repo,
        `terraform/tenants/${value}`,
      );

      // Parse HCL
      const parsed = await parseHcl(value, content);
      const modules = extractModuleBlocks(parsed);

      if (modules.length === 0) {
        throw new Error('No module blocks found in the file');
      }

      // Use the first module block that matches create-tenant source
      // (skip secondary modules like _sas)
      const tenantModule = modules.find(m =>
        m.source.includes('create-tenant'),
      ) || modules[0];
      const tenantName = tenantModule.name || tenantNameFromFile(value);

      // extractModuleBlocks returns { name, source, version, currentValues }
      const loadedConfig = hclToConfig(
        tenantModule.currentValues,
        tenantName,
        config.business_unit_repo,
      );

      // Version check — upgrade if below minimum or empty
      const currentVersion = tenantModule.version || '';
      const versionNeedsUpgrade = !currentVersion || compareVersions(currentVersion, MIN_MODULE_VERSION) < 0;
      if (versionNeedsUpgrade) {
        loadedConfig.module_version = MODULE_VERSION_CONSTRAINT;
        setVersionUpgraded(currentVersion || 'none');
      } else {
        // Preserve existing version constraint
        loadedConfig.module_version = currentVersion.startsWith('~>') ? currentVersion : `~> ${currentVersion}`;
        setVersionUpgraded(null);
      }

      onConfigLoaded(loadedConfig, value);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse tenant file');
    } finally {
      setLoadingConfig(false);
    }
  };

  return (
    <Paper variant="outlined" style={{ padding: 24 }}>
      <Box display="flex" flexDirection="column" style={{ gap: 20 }}>
        <Typography variant="h6">Edit Tenant</Typography>
        <Typography variant="body2" color="textSecondary">
          Select a business unit and tenant file to edit.
        </Typography>

        <Autocomplete
          options={selectableUnits}
          getOptionLabel={(bu) => bu.name}
          getOptionSelected={(option, value) => option.repo === value.repo}
          value={selectedBu}
          onChange={(_, value) => onBuChange(value?.repo ?? '')}
          renderOption={(bu) => (
            <Box>
              <Typography variant="body2">{bu.name}</Typography>
              <Typography variant="caption" color="textSecondary">
                {bu.repo} ({bu.short})
              </Typography>
            </Box>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label="Business Unit"
              variant="outlined"
              fullWidth
            />
          )}
        />

        {config.business_unit_repo && (
          <Autocomplete
            options={files.map(f => f.name)}
            loading={filesLoading}
            value={selectedFile || null}
            onChange={handleFileSelect}
            getOptionLabel={(name) => tenantNameFromFile(name)}
            renderInput={params => (
              <TextField
                {...params}
                label="Tenant"
                variant="outlined"
                fullWidth
                helperText={
                  files.length === 0 && !filesLoading
                    ? 'No tenant files found in terraform/tenants'
                    : 'Select a tenant to edit'
                }
              />
            )}
          />
        )}

        {loadingConfig && (
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <CircularProgress size={20} />
            <Typography>Loading tenant configuration...</Typography>
          </Box>
        )}

        {error && (
          <Typography color="error">{error}</Typography>
        )}

        {versionUpgraded !== null && (
          <Alert severity="info">
            The tenant module version ({versionUpgraded || 'not set'}) is below the minimum required version ({MIN_MODULE_VERSION}).
            It will be upgraded to <code>{MODULE_VERSION_CONSTRAINT}</code>.
          </Alert>
        )}
      </Box>
    </Paper>
  );
}
